<template>
    <div class="td-page">
        <!-- Loading State -->
        <div v-if="loading" class="td-loading">
            <div class="td-loading__spinner">
                <i class="fas fa-spinner fa-spin"></i>
            </div>
            <p>Loading task details...</p>
        </div>

        <!-- Not Found / No Access -->
        <div v-else-if="!task" class="td-empty">
            <i class="fas fa-folder-open"></i>
            <h3>Task Not Found</h3>
            <p>The task you're looking for doesn't exist or has been removed.</p>
            <button class="td-btn td-btn--primary" @click="backToTasks">
                <i class="fas fa-arrow-left"></i> Back to Tasks
            </button>
        </div>
        <div v-else-if="!isUserAssignedForIssue" class="td-empty">
            <i class="fas fa-lock"></i>
            <h3>Access Denied</h3>
            <p>You do not have permission to view this task.</p>
            <button class="td-btn td-btn--primary" @click="backToTasks">
                <i class="fas fa-arrow-left"></i> Back to Tasks
            </button>
        </div>

        <!-- Main Content -->
        <template v-else>
            <!-- Header -->
            <div class="td-header">
                <div class="td-header__left">
                    <button
                        class="td-back"
                        @click="task.taskId ? backToParentTask() : backToTasks()"
                        :title="task.taskId ? 'View Parent Task' : 'View All Tasks'"
                    >
                        <i class="fas fa-arrow-left"></i>
                    </button>
                    <div class="td-header__info">
                        <h1 class="td-header__title">{{ task.name }}</h1>
                        <div class="td-header__meta">
                            <span class="td-meta-badge td-meta-badge--reporter" v-if="task.reporterName">
                                <i class="fas fa-user"></i> Reported by {{ task.reporterName }}
                            </span>
                            <span class="td-meta-badge" v-if="task.taskId">
                                <i class="fas fa-sitemap"></i> Subtask
                            </span>
                        </div>
                    </div>
                </div>
                <div class="td-header__actions">
                    <div class="td-autosave" v-if="processingForm">
                        <i class="fas fa-spinner fa-spin"></i> Auto-saving...
                    </div>
                    <button
                        class="td-btn td-btn--primary"
                        @click="saveChanges(true)"
                        v-if="!isEditDisabled"
                        :disabled="savingRestricted"
                    >
                        <i class="fas fa-spinner fa-spin" v-if="savingRestricted"></i>
                        <i class="fas fa-save" v-else></i>
                        {{ savingRestricted ? "Saving..." : "Save Changes" }}
                    </button>
                </div>
            </div>

            <!-- Two Column Layout -->
            <div class="td-layout">
                <!-- Main Column -->
                <div class="td-main">
                    <!-- Description Card -->
                    <div class="td-card">
                        <div class="td-card__header">
                            <i class="fas fa-align-left"></i> Description
                        </div>
                        <div class="td-card__body td-card__body--flush">
                            <HtmlEditor
                                :id="`${task.name.replaceAll(' ', '_')}`"
                                @change="description = $event"
                                :value="description"
                            />
                        </div>
                    </div>

                    <!-- Assignees Card -->
                    <div class="td-card">
                        <div class="td-card__header">
                            <i class="fas fa-users"></i> Assignees
                        </div>
                        <div class="td-card__body">
                            <SelectDropdown
                                :id="`issue-assignees`"
                                :options="
                                    taskCreateOptions.assignees.map((item) => ({
                                        id: item.id,
                                        title: item.firstName,
                                    }))
                                "
                                :multiple="true"
                                :field="'issue-assignees'"
                                :value="assigneeUserIds"
                                :disabled="isEditDisabled"
                                @change="assigneeUserIds = $event"
                            />
                            <TagUser
                                v-if="task && !isEditDisabled"
                                :updateRoute="'/issue/' + task.id"
                                :taggedUsers="task.taggedUsers || []"
                                @update="updateTaggedUsers"
                            />
                        </div>
                    </div>

                    <!-- Attachments Card -->
                    <div class="td-card" v-if="showAttachmentManager">
                        <div class="td-card__header">
                            <i class="fas fa-paperclip"></i> Attachments
                        </div>
                        <div class="td-card__body">
                            <div class="td-attachments">
                                <AttachmentUploader
                                    @remove="addToRemoveList('attachment1')"
                                    v-model="attachment1"
                                    :id="1"
                                    :name="attachment1Name"
                                    :isUser="isEditDisabled"
                                />
                                <AttachmentUploader
                                    @remove="addToRemoveList('attachment2')"
                                    v-model="attachment2"
                                    :id="2"
                                    :name="attachment2Name"
                                    :isUser="isEditDisabled"
                                />
                                <AttachmentUploader
                                    @remove="addToRemoveList('attachment3')"
                                    v-model="attachment3"
                                    :id="3"
                                    :name="attachment3Name"
                                    :isUser="isEditDisabled"
                                />
                                <AttachmentUploader
                                    @remove="addToRemoveList('attachment4')"
                                    v-model="attachment4"
                                    :id="4"
                                    :name="attachment4Name"
                                    :isUser="isEditDisabled"
                                />
                                <AttachmentUploader
                                    @remove="addToRemoveList('attachment5')"
                                    v-model="attachment5"
                                    :id="5"
                                    :name="attachment5Name"
                                    :isUser="isEditDisabled"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Subtasks Card -->
                    <div class="td-card" v-if="!task.taskId">
                        <div class="td-card__header">
                            <i class="fas fa-sitemap"></i> Subtasks
                            <button
                                class="td-btn td-btn--sm td-btn--outline"
                                @click="
                                    openLink(
                                        `/project-tasks/create?task_id=${task.id}&company_id=${
                                            taskCreateOptions.activeCompany
                                                ? taskCreateOptions.activeCompany.id
                                                : ''
                                        }&project_id=${task.projectId}`
                                    )
                                "
                            >
                                <i class="fas fa-plus"></i> Add Subtask
                            </button>
                        </div>
                        <div class="td-card__body" v-if="subTasks && subTasks.length">
                            <div
                                class="td-subtask"
                                v-for="subTask of subTasks"
                                :key="subTask.id"
                            >
                                <div class="td-subtask__info">
                                    <div class="td-subtask__name">{{ subTask.name }}</div>
                                    <div class="td-subtask__date">
                                        <i class="fas fa-calendar-alt"></i>
                                        {{ formatIndianDate(subTask.createdAt) }}
                                    </div>
                                </div>
                                <div class="td-subtask__actions">
                                    <button
                                        class="td-icon-btn td-icon-btn--edit"
                                        @click="openLink(`/project-tasks/${subTask.slug}`)"
                                        title="Edit Subtask"
                                    >
                                        <i class="fas fa-external-link-alt"></i>
                                    </button>
                                    <button
                                        class="td-icon-btn td-icon-btn--delete"
                                        @click="deleteTask(subTask.id)"
                                        title="Delete Subtask"
                                    >
                                        <i class="fas fa-trash-alt"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="td-card__body" v-else>
                            <div class="td-empty-section">
                                <i class="fas fa-inbox"></i>
                                <span>No subtasks yet</span>
                            </div>
                        </div>
                    </div>

                    <!-- Comments Card -->
                    <div class="td-card">
                        <div class="td-card__header">
                            <i class="fas fa-comments"></i> Comments
                        </div>
                        <div class="td-card__body td-card__body--flush">
                            <CommentSystem :taskId="task.id" />
                        </div>
                    </div>
                </div>

                <!-- Sidebar -->
                <div class="td-sidebar">
                    <!-- Status Card -->
                    <div class="td-card">
                        <div class="td-card__header">
                            <i class="fas fa-flag"></i> Status
                        </div>
                        <div class="td-card__body">
                            <SelectDropdown
                                :id="`issue-status`"
                                :options="
                                    taskCreateOptions.projectStatuses.map((item) => ({
                                        id: item.id,
                                        title: item.name,
                                    }))
                                "
                                :multiple="false"
                                :field="'issue-status'"
                                :value="taskStatusId"
                                @change="taskStatusId = $event"
                            />
                        </div>
                    </div>

                    <!-- Details Card -->
                    <div class="td-card">
                        <div class="td-card__header">
                            <i class="fas fa-info-circle"></i> Details
                        </div>
                        <div class="td-card__body td-sidebar-fields">
                            <!-- Departments -->
                            <div class="td-field">
                                <label class="td-field__label">
                                    <i class="fas fa-building"></i> Departments
                                </label>
                                <div class="td-field__value">
                                    <SelectDropdown
                                        :id="`issue-departments`"
                                        :options="
                                            taskCreateOptions.departments.map((item) => ({
                                                id: item.id,
                                                title: item.name,
                                            }))
                                        "
                                        :multiple="true"
                                        :field="'issue-departments'"
                                        :value="departmentIds"
                                        :disabled="isEditDisabled"
                                        @change="departmentIds = $event"
                                    />
                                </div>
                            </div>

                            <!-- Pending Due To -->
                            <div
                                class="td-field"
                                v-if="
                                    taskStatusId ==
                                    taskCreateOptions.projectStatuses.find(
                                        (item) => item.slug == 'pending'
                                    )?.id
                                "
                            >
                                <label class="td-field__label">
                                    <i class="fas fa-exclamation-triangle"></i> Pending Due To
                                </label>
                                <div class="td-field__value">
                                    <SelectDropdown
                                        :id="`issue-pending-task`"
                                        :options="
                                            taskCreateOptions.allUsers.map((item) => ({
                                                id: item.id,
                                                title: item.firstName,
                                            }))
                                        "
                                        :multiple="true"
                                        :field="'issue-pending-task'"
                                        :value="pendingDueToUserIds"
                                        :disabled="isEditDisabled"
                                        @change="pendingDueToUserIds = $event"
                                    />
                                </div>
                            </div>

                            <!-- Start Date -->
                            <div class="td-field" v-if="startDate">
                                <label class="td-field__label">
                                    <i class="fas fa-play-circle"></i> Start Date
                                </label>
                                <div class="td-field__value td-field__value--text">
                                    {{ new Date(startDate).toDateString() }}
                                </div>
                            </div>

                            <!-- Target Date -->
                            <div class="td-field" v-if="endDate">
                                <label class="td-field__label">
                                    <i class="fas fa-bullseye"></i> Target Date
                                </label>
                                <div class="td-field__value">
                                    <input
                                        class="td-date-input"
                                        type="date"
                                        :min="new Date(startDate).toISOString().slice(0, 10)"
                                        :value="new Date(endDate).toISOString().slice(0, 10)"
                                        @change="endDate = $event.target.value"
                                        :disabled="isEditDisabled"
                                    />
                                </div>
                            </div>

                            <!-- Creator -->
                            <div class="td-field">
                                <label class="td-field__label">
                                    <i class="fas fa-user-shield"></i> Creator
                                </label>
                                <div class="td-field__value">
                                    <SelectDropdown
                                        :id="`issue-reporter`"
                                        :options="
                                            taskCreateOptions.allUsers.map((item) => ({
                                                id: item.id,
                                                title: item.firstName,
                                            }))
                                        "
                                        :multiple="false"
                                        :field="'issue-reporter'"
                                        :value="reporterId"
                                        :disabled="isEditDisabled"
                                        @change="reporterId = $event"
                                    />
                                </div>
                            </div>

                            <!-- Priority -->
                            <div class="td-field">
                                <label class="td-field__label">
                                    <i class="fas fa-bolt"></i> Priority
                                </label>
                                <div class="td-field__value">
                                    <SelectDropdown
                                        :id="`issue-priority`"
                                        :options="
                                            taskCreateOptions.projectPriorities.map((item) => ({
                                                id: item.id,
                                                title: item.name,
                                            }))
                                        "
                                        :multiple="false"
                                        :field="'issue-priority'"
                                        :value="priorityId"
                                        :disabled="isEditDisabled"
                                        @change="priorityId = $event"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import HtmlEditor from "@/components/other/HtmlEditor"
import Button from "@/components/UI/Button"
import SelectDropdown from "@/components/crud/SelectDropdown"
import CommentSystem from "@/components/Lists/CommentSystem"
import TagUser from "@/components/other/TagUser"
import { mapState, mapActions } from "vuex"
import { errMsg, hasIssuePermission } from "@/utils/common"
import AttachmentUploader from "@/components/other/AttachmentUploader"
import { http } from "@/utils/http"
import { formatIndianDate } from "../../../utils/common"

export default {
    components: {
        HtmlEditor,
        Button,
        SelectDropdown,
        CommentSystem,
        AttachmentUploader,
        TagUser,
    },
    computed: {
        ...mapState("project", ["projects", "projectStatuses"]),
        ...mapState("auth", ["user", "isAdmin"]),
        isCreatorOrAdmin() {
            return true
            // condition later enable
            // (this.task.Reporter && this.user.id == this.task.Reporter.id) || this.isAdmin
        },
    },
    data() {
        return {
            showAttachmentManager: true,
            subTasks: [],
            taskId: "",
            loading: false,
            taskCreateOptions: null,
            description: "",
            task: null,
            processingForm: false,
            taskStatusId: "",
            assigneeUserIds: [],
            departmentIds: [],
            reporterId: null,
            priorityId: null,
            savingRestricted: false,
            isEditDisabled: false,
            isUserAssignedForIssue: false,
            attachment1: null,
            attachment2: null,
            attachment3: null,
            attachment4: null,
            attachment5: null,
            attachment1Name: "",
            attachment2Name: "",
            attachment3Name: "",
            attachment4Name: "",
            attachment5Name: "",
            removeFilesList: [],
            startDate: "",
            endDate: "",
            interimDate: "",
            reportToIds: [],
            pendingDueToUserIds: [],
        }
    },
    async mounted() {
        window.document.title = "TASK | Sub TASK"
        this.updateTaskDetails(true)
    },
    methods: {
        backToParentTask() {
            if (this.taskCreateOptions && this.taskCreateOptions.parentTask) {
                window.location.href = `/project-tasks/${this.taskCreateOptions.parentTask.slug}`
            }
        },
        async deleteTask(id) {
            try {
                if (confirm("Are you sure?")) {
                    await http.delete(`/project-tasks/${id}`)
                    window.location.reload()
                }
            } catch (err) {
                console.log("error in deleting task", err)
            }
        },
        formatIndianDate,
        ...mapActions("auth", [
            // "checkRole"
        ]),
        backToTasks() {
            this.$router.push("/project-tasks")
            // this.$router.goBack(-1)
        },
        openLink(url) {
            window.open(url, "_blank")
        },
        updateTaggedUsers(ticketUpdate) {
            if (ticketUpdate) {
                console.log("ticket update", ticketUpdate)
                this.task.taggedUsers = ticketUpdate.taggedUsers
            }
        },
        async updateTaskDetails(fetchData) {
            this.loading = true

            if (fetchData) {
                const taskCreateOptions = await this.fetchTaskCreateOpotions()
                this.taskCreateOptions = taskCreateOptions
                this.task = taskCreateOptions.task
            }

            if (this.task) {
                const descriptionData =
                    this.task.description && this.task.description != "undefined"
                        ? this.task.description
                        : ""
                this.description = descriptionData
                this.originalDescription = descriptionData
                this.assigneeUserIds = this.task.assigneeUserIds
                this.departmentIds = this.task.departmentIds || []
                this.reporterId = this.task.reporterId
                this.priorityId = this.task.priorityId
                this.taskStatusId = this.task.statusId
                this.taskId = this.task.id
                this.subTasks = this.taskCreateOptions.subTasks

                this.startDate = this.task.startDate
                this.endDate = this.task.endDate
                this.interimDate = this.task.interimDate
                this.reportToIds = this.task.reportToIds
                this.pendingDueToUserIds = this.task.pendingDueToUserIds

                this.attachment1Name = this.task.attachment1
                this.attachment2Name = this.task.attachment2
                this.attachment3Name = this.task.attachment3
                this.attachment4Name = this.task.attachment4
                this.attachment5Name = this.task.attachment5
            }
            // todo later uncomment below condition
            this.isEditDisabled = false
            // this.isEditDisabled = await this.checkRole({ user: this.user, roleToCheck: "User" })

            // todo later uncomment below condition
            this.isUserAssignedForIssue = true

            // this.isUserAssignedForIssue =
            //     this.isAdmin ||
            //     hasIssuePermission(
            //         this.user.id,
            //         this.task.AssigneeUser,
            //         this.task.Reporter && this.task.Reporter.id
            //     )
            this.loading = false
        },
        async fetchTaskCreateOpotions() {
            let result = {}
            try {
                const res = await http.get(`/project-tasks/init?task_slug=${this.$route.params.id}`)
                result = res.data
            } catch (err) {
                console.log("error in loading customers", err)
            }
            this.initialized = true
            return result
        },
        async saveChanges(restricted = false) {
            let result = {}
            if (restricted) {
                if (this.savingRestricted) return
                this.savingRestricted = true
            }
            if (this.processingForm) return
            this.processingForm = true
            if (
                this.attachment1 ||
                this.attachment2 ||
                this.attachment3 ||
                this.attachment4 ||
                this.attachment5
            ) {
                this.showAttachmentManager = false
            }
            try {
                const formData = new FormData()

                formData.append("description", this.description || "")
                formData.append("assigneeUserIds", JSON.stringify(this.assigneeUserIds) || [])
                formData.append("departmentIds", JSON.stringify(this.departmentIds || []))
                formData.append(
                    "pendingDueToUserIds",
                    JSON.stringify(this.pendingDueToUserIds || [])
                )
                // formData.append("taskId", this.taskId || "")
                formData.append("priorityId", this.priorityId || "")
                formData.append("reporterId", this.reporterId || "")
                formData.append("statusId", this.taskStatusId || "")
                formData.append("endDate", this.endDate || "")
                formData.append("interimDate", this.interimDate || "")

                // files to remove if any
                formData.append("removeFilesList", JSON.stringify(this.removeFilesList))

                // attachments to add if any
                formData.append("attachment1", this.attachment1 || "")
                formData.append("attachment2", this.attachment2 || "")
                formData.append("attachment3", this.attachment3 || "")
                formData.append("attachment4", this.attachment4 || "")
                formData.append("attachment5", this.attachment5 || "")

                const res = await http.patch(
                    `/project-tasks/${this.task && this.task.id}`,
                    formData
                )
                result = res.data.result
                this.task = result
                await this.updateTaskDetails(false)
                if (restricted) {
                    this.$toast.info("Saved Changes")
                }
            } catch (err) {
                console.log("error in loading customers", err)
                this.$toast.error("Failed to update!")
                this.$toast.error(errMsg(err))
            }
            this.showAttachmentManager = true
            this.savingRestricted = false
            this.processingForm = false
            this.initialized = true
            return result
        },
        async addToRemoveList(item) {
            if (!this.removeFilesList.includes(item)) {
                this.removeFilesList = [...this.removeFilesList, item]
            }
            console.log("remove files list", this.removeFilesList)
        },
    },
    watch: {
        priorityId(e) {
            this.saveChanges()
        },
        taskStatusId(e) {
            this.saveChanges()
        },
        reporterId(e) {
            this.saveChanges()
        },
        assigneeUserIds(e) {
            this.saveChanges()
        },
        departmentIds(e) {
            this.saveChanges()
        },
        pendingDueToUserIds(e) {
            this.saveChanges()
        },
        taskId(e) {
            this.saveChanges()
        },
        interimDate(e) {
            this.saveChanges()
        },
        endDate(e) {
            this.saveChanges()
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";

.td-page {
    animation: tdFade .25s ease;
}
@keyframes tdFade {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}

/* ── Loading / Empty States ── */
.td-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
    color: $gray400;

    &__spinner {
        font-size: 28px;
        margin-bottom: 12px;
        color: $primaryColor;
    }
    p { margin: 0; font-size: 14px; }
}
.td-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
    text-align: center;

    > i {
        font-size: 40px;
        color: $gray300;
        margin-bottom: 16px;
    }
    h3 {
        font-size: 18px;
        font-weight: 700;
        color: $gray700;
        margin: 0 0 6px;
    }
    p {
        font-size: 13px;
        color: $gray400;
        margin: 0 0 20px;
    }
}

/* ── Header ── */
.td-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 20px;
    gap: 16px;
    flex-wrap: wrap;

    &__left {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        flex: 1;
        min-width: 0;
    }
    &__info { min-width: 0; flex: 1; }
    &__title {
        font-size: 20px;
        font-weight: 700;
        color: $gray800;
        margin: 0;
        line-height: 1.3;
        word-break: break-word;
    }
    &__meta {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 6px;
        flex-wrap: wrap;
    }
    &__actions {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-shrink: 0;
    }
}
.td-back {
    width: 36px;
    height: 36px;
    border-radius: $radiusSm;
    border: 1px solid $gray200;
    background: white;
    color: $gray600;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
    transition: all $transitionFast;
    flex-shrink: 0;
    margin-top: 2px;

    &:hover { background: $gray50; border-color: $gray300; color: $gray800; }
}
.td-meta-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 500;
    background: $gray100;
    color: $gray600;

    i { font-size: 10px; }

    &--reporter {
        background: $primaryLight;
        color: $primaryColor;
    }
}
.td-autosave {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: $gray400;
    i { color: $primaryColor; }
}

/* ── Layout ── */
.td-layout {
    display: grid;
    grid-template-columns: 1fr 330px;
    gap: 20px;
    align-items: start;
}

/* ── Cards ── */
.td-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusMd;
    box-shadow: $shadowSm;
    margin-bottom: 16px;
    overflow: visible;
    position: relative;
    z-index: 1;

    &:focus-within { z-index: 100; }

    &__header {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        border-bottom: 1px solid $gray100;
        background: $gray50;
        font-size: 13px;
        font-weight: 700;
        color: $gray800;

        > i { color: $primaryColor; font-size: 13px; }

        .td-btn { margin-left: auto; }
    }
    &__body {
        padding: 16px;

        &--flush { padding: 0; }
    }
}

/* ── Attachments ── */
.td-attachments {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

/* ── Subtasks ── */
.td-subtask {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid $gray100;
    gap: 12px;
    transition: background $transitionFast;

    &:last-child { border-bottom: none; }
    &:hover { background: $gray50; }

    &__info {
        flex: 1;
        min-width: 0;
    }
    &__name {
        font-size: 13px;
        font-weight: 600;
        color: $gray800;
        margin-bottom: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    &__date {
        font-size: 11px;
        color: $gray400;
        i { margin-right: 3px; }
    }
    &__actions {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-shrink: 0;
    }
}

/* ── Empty Section ── */
.td-empty-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 16px;
    gap: 8px;
    color: $gray400;
    font-size: 13px;
    i { font-size: 22px; color: $gray300; }
}

/* ── Sidebar Fields ── */
.td-sidebar-fields {
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.td-field {
    position: relative;
    z-index: 1;
    &:focus-within { z-index: 100; }

    &__label {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        font-weight: 700;
        color: $gray500;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        margin-bottom: 6px;
        i { font-size: 10px; color: $gray400; }
    }
    &__value {
        &--text {
            font-size: 13px;
            color: $gray700;
            font-weight: 500;
        }
    }
}
.td-date-input {
    width: 100%;
    padding: 7px 10px;
    border: 1px solid $gray200;
    border-radius: $radiusSm;
    font-size: 13px;
    font-family: inherit;
    color: $gray700;
    background: white;
    transition: border-color $transitionFast;
    outline: none;

    &:focus { border-color: $primaryColor; }
    &:disabled { background: $gray50; color: $gray400; cursor: not-allowed; }
}

/* ── Buttons ── */
.td-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 18px;
    border-radius: $radiusSm;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    border: none;
    transition: all $transitionFast;
    white-space: nowrap;

    &:disabled { opacity: 0.55; cursor: not-allowed; }

    &--primary {
        background: $primaryColor;
        color: white;
        &:hover:not(:disabled) { background: $primaryDark; }
    }
    &--outline {
        background: white;
        color: $primaryColor;
        border: 1px solid lighten($primaryColor, 30%);
        &:hover { background: $primaryLight; }
    }
    &--sm {
        padding: 5px 12px;
        font-size: 12px;
    }
}

/* ── Icon Buttons ── */
.td-icon-btn {
    width: 30px;
    height: 30px;
    border-radius: $radiusSm;
    border: 1px solid $gray200;
    background: white;
    color: $gray500;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 12px;
    transition: all $transitionFast;

    &--edit:hover {
        background: $primaryLight;
        border-color: lighten($primaryColor, 30%);
        color: $primaryColor;
    }
    &--delete:hover {
        background: lighten($danger, 38%);
        border-color: lighten($danger, 25%);
        color: $danger;
    }
}

/* ── Responsive ── */
@media (max-width: 1024px) {
    .td-layout {
        grid-template-columns: 1fr;
    }
}
@media (max-width: 640px) {
    .td-header {
        flex-direction: column;
    }
    .td-header__actions {
        width: 100%;
        justify-content: flex-end;
    }
}
</style>
